import React, { useState, useEffect } from 'react';
import { useApp } from '@/context/AppContext';
import axios from 'axios';
import {
  Users,
  Plus,
  MagnifyingGlass,
  Phone,
  Envelope,
  Star,
  Gift,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PromotionsManager } from '@/components/PromotionsManager';
import { API_URL as __API_BASE__ } from '@/lib/apiBase';
const API_URL = __API_BASE__;

export const CustomersPage = () => {
  const { currentOutlet } = useApp();
  const [customers, setCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const [customerForm, setCustomerForm] = useState({
    name: '',
    phone: '',
    email: '',
  });

  useEffect(() => {
    if (currentOutlet) {
      fetchCustomers();
    }
  }, [currentOutlet]);

  const fetchCustomers = async (search = '') => {
    try {
      let url = `${API_URL}/api/customers?outlet_id=${currentOutlet.id}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const response = await axios.get(url, { withCredentials: true });
      setCustomers(response.data);
    } catch (e) {
      console.error('Error fetching customers:', e);
    }
  };

  const handleSearch = () => {
    fetchCustomers(searchQuery);
  };

  const handleCreateCustomer = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/customers`, {
        ...customerForm,
        outlet_id: currentOutlet.id,
      }, { withCredentials: true });
      await fetchCustomers();
      setShowCustomerModal(false);
      resetForm();
    } catch (e) {
      console.error('Error creating customer:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleViewCustomer = async (customer) => {
    try {
      const response = await axios.get(`${API_URL}/api/customers/${customer.id}`, { withCredentials: true });
      setSelectedCustomer(response.data);
    } catch (e) {
      console.error('Error fetching customer:', e);
    }
  };

  const resetForm = () => {
    setCustomerForm({
      name: '',
      phone: '',
      email: '',
    });
  };

  if (!currentOutlet) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Please select an outlet to manage customers</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="customers-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold">Customers & Loyalty</h1>
          <p className="text-muted-foreground mt-1">{currentOutlet?.name}</p>
        </div>
        <Button
          onClick={() => { resetForm(); setShowCustomerModal(true); }}
          className="bg-primary hover:bg-primary-hover text-white"
          data-testid="add-customer-btn"
        >
          <Plus size={18} className="mr-2" />
          Add Customer
        </Button>
      </div>

      {/* Search */}
      <div className="flex gap-2 max-w-md">
        <div className="relative flex-1">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <Input
            placeholder="Search by name, phone, or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="pl-10"
            data-testid="customer-search"
          />
        </div>
        <Button onClick={handleSearch} variant="outline">Search</Button>
      </div>

      {/* Promotions Manager */}
      {currentOutlet && <PromotionsManager outletId={currentOutlet.id} />}

      {/* Customers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {customers.map((customer) => (
          <div
            key={customer.id}
            className="bg-white rounded-lg border border-border p-4 hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => handleViewCustomer(customer)}
            data-testid={`customer-${customer.id}`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Users size={24} className="text-primary" />
                </div>
                <div>
                  <h3 className="font-heading font-bold">{customer.name}</h3>
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <Phone size={14} />
                    {customer.phone}
                  </p>
                </div>
              </div>
            </div>
            
            {customer.email && (
              <p className="text-sm text-muted-foreground flex items-center gap-1 mb-3">
                <Envelope size={14} />
                {customer.email}
              </p>
            )}

            <div className="flex items-center justify-between pt-3 border-t border-border">
              <div className="flex items-center gap-1 text-accent-yellow">
                <Star size={16} weight="fill" />
                <span className="font-medium">{customer.loyalty_points || 0} pts</span>
              </div>
              <span className="text-xs text-muted-foreground">
                {customer.total_visits || 0} visits
              </span>
            </div>
          </div>
        ))}
        
        {customers.length === 0 && (
          <div className="col-span-full text-center py-12 text-muted-foreground">
            <Users size={48} className="mx-auto mb-4 opacity-30" />
            <p>No customers found</p>
          </div>
        )}
      </div>

      {/* Customer Detail Dialog */}
      {selectedCustomer && (
        <Dialog open={!!selectedCustomer} onOpenChange={() => setSelectedCustomer(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">{selectedCustomer.name}</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-4 p-4 bg-surface rounded-lg">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <Users size={32} className="text-primary" />
                </div>
                <div>
                  <p className="font-medium">{selectedCustomer.phone}</p>
                  {selectedCustomer.email && (
                    <p className="text-sm text-muted-foreground">{selectedCustomer.email}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-4 bg-accent-yellow/10 rounded-lg">
                  <Star size={24} className="mx-auto text-accent-yellow mb-2" weight="fill" />
                  <p className="text-2xl font-heading font-bold">{selectedCustomer.loyalty_points || 0}</p>
                  <p className="text-xs text-muted-foreground">Loyalty Points</p>
                </div>
                <div className="p-4 bg-primary/10 rounded-lg">
                  <Users size={24} className="mx-auto text-primary mb-2" />
                  <p className="text-2xl font-heading font-bold">{selectedCustomer.total_visits || 0}</p>
                  <p className="text-xs text-muted-foreground">Total Visits</p>
                </div>
                <div className="p-4 bg-accent-green/10 rounded-lg">
                  <Gift size={24} className="mx-auto text-accent-green mb-2" />
                  <p className="text-2xl font-heading font-bold">${(selectedCustomer.total_spent || 0).toFixed(0)}</p>
                  <p className="text-xs text-muted-foreground">Total Spent</p>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Add Customer Modal */}
      <Dialog open={showCustomerModal} onOpenChange={setShowCustomerModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Add Customer</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={customerForm.name}
                onChange={(e) => setCustomerForm({ ...customerForm, name: e.target.value })}
                placeholder="Customer name"
                data-testid="customer-name"
              />
            </div>

            <div className="space-y-2">
              <Label>Phone *</Label>
              <Input
                value={customerForm.phone}
                onChange={(e) => setCustomerForm({ ...customerForm, phone: e.target.value })}
                placeholder="+1 555 0100"
                data-testid="customer-phone"
              />
            </div>

            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={customerForm.email}
                onChange={(e) => setCustomerForm({ ...customerForm, email: e.target.value })}
                placeholder="customer@example.com"
              />
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t border-border">
            <Button
              variant="outline"
              onClick={() => setShowCustomerModal(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateCustomer}
              className="flex-1 bg-primary hover:bg-primary-hover text-white"
              disabled={loading || !customerForm.name || !customerForm.phone}
              data-testid="save-customer-btn"
            >
              {loading ? 'Creating...' : 'Add Customer'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomersPage;
